import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { useAuth } from '@/context/AuthContext';

const condensed = { fontFamily: 'Barlow Condensed' };

export const SportHero = ({ page }) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const getStarted = () => navigate(isAuthenticated ? '/create' : '/login?mode=register&redirect=/create');
  return (
    <section className="bg-hero-gradient relative overflow-hidden" data-testid="sport-hero">
      <div className="absolute inset-0 noise-overlay" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7 animate-fade-in-up">
          <p className="text-sm tracking-[0.25em] uppercase font-bold text-amber-300 mb-3" style={condensed} data-testid="sport-eyebrow">
            {page.eyebrow}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold uppercase leading-[0.95] text-white mb-5" style={condensed} data-testid="sport-headline">
            Run Your {page.label} League<br /><span className="text-[#CF142B]">From One Platform.</span>
          </h1>
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-xl mb-8" data-testid="sport-intro">{page.intro}</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button size="lg" onClick={getStarted} data-testid="sport-get-started-btn"
              className="bg-white text-[#00247D] hover:bg-slate-100 btn-transition h-14 px-8 text-base font-bold rounded-md">
              Get Started FREE <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button size="lg" variant="outline" onClick={() => navigate('/create')} data-testid="sport-free-schedule-btn"
              className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white h-14 px-8 text-base font-bold rounded-md">
              Create a Free {page.label} Schedule
            </Button>
          </div>
          <p className="text-sm text-slate-400 mt-4">Your League. One Platform. No credit card required.</p>
        </div>
        <div className="lg:col-span-5 animate-fade-in-up">
          <div className="relative rounded-2xl overflow-hidden border border-white/15 shadow-2xl aspect-[4/3]">
            <img src={page.image} alt={page.imageAlt} className="w-full h-full object-cover" data-testid="sport-hero-image" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F2C]/60 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

export const SportFeatures = ({ page }) => (
  <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24" data-testid="sport-features">
    <div className="max-w-3xl mb-12">
      <span className="text-sm tracking-[0.2em] uppercase font-bold text-[#CF142B]" style={condensed}>Built for {page.label}</span>
      <h2 className="text-3xl md:text-5xl tracking-tight font-bold uppercase text-[#00247D] mt-3 leading-[0.95]" style={condensed}>
        Everything Your {page.label} League Needs
      </h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
      {page.features.map((f, i) => (
        <div key={f.title} className="group p-6 rounded-2xl bg-slate-50 border border-slate-200 hover:-translate-y-1 hover:border-[#00247D]/30 hover:shadow-card-hover transition-[transform,border-color,box-shadow] duration-300" data-testid={`sport-feature-${i}`}>
          <div className="w-11 h-11 rounded-xl bg-[#00247D] flex items-center justify-center mb-4 group-hover:bg-[#CF142B] transition-colors duration-300">
            <f.icon className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-xl font-bold uppercase tracking-wide text-[#00247D]" style={condensed}>{f.title}</h3>
          <p className="text-sm text-slate-600 leading-relaxed mt-1.5">{f.desc}</p>
        </div>
      ))}
    </div>
  </section>
);

export const SportSteps = ({ page }) => (
  <section className="bg-slate-50 border-y border-slate-200" data-testid="sport-steps">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-3 gap-8">
      {page.steps.map((s, i) => (
        <div key={s.title} className="flex gap-4">
          <span className="text-5xl font-bold text-[#CF142B] leading-none" style={condensed}>{i + 1}</span>
          <div>
            <h3 className="text-xl font-bold uppercase text-[#00247D]" style={condensed}>{s.title}</h3>
            <p className="text-sm text-slate-600 mt-1">{s.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </section>
);

export const SportFaq = ({ page }) => (
  <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="sport-faq">
    <h2 className="text-3xl md:text-4xl tracking-tight font-bold uppercase text-[#00247D] mb-6" style={condensed}>
      {page.label} League FAQ
    </h2>
    <Accordion type="single" collapsible className="w-full">
      {page.faqs.map((f, i) => (
        <AccordionItem key={f.question} value={`q${i}`} data-testid={`sport-faq-${i}`}>
          <AccordionTrigger className="text-left font-semibold text-slate-900">{f.question}</AccordionTrigger>
          <AccordionContent className="text-slate-600 leading-relaxed">{f.answer}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
    <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
      {['Free round robin schedules', 'Works on phone, tablet and computer', 'Upgrade only when you need more'].map((t) => (
        <li key={t} className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#009E49]" />{t}</li>
      ))}
    </ul>
  </section>
);
